import type { CmsEditingContext } from '@eneco/cms-adapter';
import { createDataAttribute } from '@sanity/visual-editing/next-pages-router';

/**
 * Builds the `data-sanity` factory the render host hands to `CmsProvider`.
 *
 * Bound once per page to the document the page came from, so the render tree only has to
 * supply the field path it already carries (`CmsRendering.fieldPath`,
 * `CmsRendering.placeholderPaths`). Keeping the binding here is what lets `@eneco/cms`
 * stay provider-neutral — it receives a plain `(path, document?) => string | undefined`.
 */
export const createEditAttribute = (
  editing: CmsEditingContext,
): ((path: string, document?: { id: string; type: string }) => string) => {
  const shared = {
    baseUrl: editing.studioUrl,
    // Omitting this makes `createEditUrl` assume the `default` workspace and drop the segment,
    // which a Studio with named workspaces answers with "Studio workspace not found".
    workspace: editing.workspace,
  };
  const forDocument = (id: string, type: string) => createDataAttribute({ id, type, ...shared });

  // With `id` and `type` bound but no `path`, the builder is itself the `(path) => string`
  // function we want — see `CreateDataAttribute` in `@sanity/visual-editing-csm`.
  const own = forDocument(editing.documentId, editing.documentType);

  // A field of a document the page merely inlined (`siteWideContent->{ … }`) names that document,
  // not the page's: the same path in the page document would point at a reference.
  return (path, document) => (document ? forDocument(document.id, document.type)(path) : own(path));
};
